import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FileText, Download, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useBusinesses } from "@/hooks/use-businesses";
import { Sidebar } from "@/components/ui/sidebar";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const calculateDateRange = (period: string) => {
  const now = new Date();
  let startDate: string, endDate: string;

  switch (period) {
    case "this-month":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-month":
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate = lastMonth.toISOString().split('T')[0];
      endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
      break;
    case "this-quarter":
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      startDate = quarterStart.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-quarter":
      const lastQuarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
      const lastQuarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0);
      startDate = lastQuarterStart.toISOString().split('T')[0];
      endDate = lastQuarterEnd.toISOString().split('T')[0];
      break;
    case "this-year":
      startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-year":
      startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
      endDate = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
      break;
    default: // "all-dates"
      startDate = "2020-01-01";
      endDate = "2030-12-31";
      break;
  }

  return { startDate, endDate };
};

export default function BalanceSheetReport() {
  const { activeBusiness } = useBusinesses();
  const [selectedPeriod, setSelectedPeriod] = useState("all-dates");
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = calculateDateRange(selectedPeriod);

  const { data: balanceSheetData, refetch } = useQuery({
    queryKey: ['/api/businesses', activeBusiness?.id, 'balance-sheet', selectedPeriod],
    queryFn: () => 
      fetch(`/api/businesses/${activeBusiness?.id}/balance-sheet?startDate=${startDate}&endDate=${endDate}`)
        .then(res => res.json()),
    enabled: !!activeBusiness?.id,
  });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const exportReport = (format: string) => {
    if (format === 'pdf') {
      exportToPDF();
    }
  };

  const exportToPDF = () => {
    if (!balanceSheetData || !activeBusiness) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(activeBusiness.name, 20, 25);
    
    doc.setFontSize(16);
    doc.text('Balance Sheet', 20, 35);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`As of: ${balanceSheetData.date} | Generated: ${new Date().toLocaleDateString()}`, 20, 45);
    
    // Horizontal line
    doc.setLineWidth(0.5);
    doc.line(20, 50, 190, 50);
    
    let yPos = 60;
    
    // ASSETS Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 100, 0);
    doc.text('ASSETS', 20, yPos);
    yPos += 10;
    
    // Current Assets
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Current Assets', 25, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (balanceSheetData.assets?.currentAssets) {
      balanceSheetData.assets.currentAssets.forEach((asset: any) => {
        doc.text(`  ${asset.name}`, 30, yPos);
        doc.text(`$${asset.value.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Current Assets', 30, yPos);
    doc.text(`$${balanceSheetData.assets?.totalCurrentAssets?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 10;
    
    // Fixed Assets
    doc.setFontSize(11);
    doc.text('Fixed Assets', 25, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (balanceSheetData.assets?.fixedAssets) {
      balanceSheetData.assets.fixedAssets.forEach((asset: any) => {
        doc.text(`  ${asset.name}`, 30, yPos);
        doc.text(`$${asset.value.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Fixed Assets', 30, yPos);
    doc.text(`$${balanceSheetData.assets?.totalFixedAssets?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 8;
    
    doc.setFontSize(12);
    doc.text('TOTAL ASSETS', 25, yPos);
    doc.text(`$${balanceSheetData.assets?.totalAssets?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 20;
    
    // LIABILITIES Section
    doc.setFontSize(14);
    doc.setTextColor(128, 0, 0);
    doc.text('LIABILITIES', 20, yPos);
    yPos += 10;
    
    // Current Liabilities
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Current Liabilities', 25, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (balanceSheetData.liabilities?.currentLiabilities) {
      balanceSheetData.liabilities.currentLiabilities.forEach((liability: any) => {
        doc.text(`  ${liability.name}`, 30, yPos);
        doc.text(`$${liability.value.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Current Liabilities', 30, yPos);
    doc.text(`$${balanceSheetData.liabilities?.totalCurrentLiabilities?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 10;
    
    // Long-term Liabilities
    doc.setFontSize(11);
    doc.text('Long-term Liabilities', 25, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (balanceSheetData.liabilities?.longTermLiabilities) {
      balanceSheetData.liabilities.longTermLiabilities.forEach((liability: any) => {
        doc.text(`  ${liability.name}`, 30, yPos);
        doc.text(`$${liability.value.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Long-term Liabilities', 30, yPos);
    doc.text(`$${balanceSheetData.liabilities?.totalLongTermLiabilities?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 8;
    
    doc.setFontSize(12);
    doc.text('TOTAL LIABILITIES', 25, yPos);
    doc.text(`$${balanceSheetData.liabilities?.totalLiabilities?.toLocaleString() || '0'}`, 150, yPos);
    yPos += 20;
    
    // EQUITY Section
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 128);
    doc.text('EQUITY', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    if (balanceSheetData.equity?.items) {
      balanceSheetData.equity.items.forEach((item: any) => {
        doc.text(`  ${item.name}`, 30, yPos);
        doc.text(`$${item.value.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 5;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.line(20, yPos, 180, yPos);
    yPos += 8;
    doc.text('TOTAL EQUITY', 25, yPos);
    doc.text(`$${balanceSheetData.equity?.totalEquity?.toLocaleString() || '0'}`, 150, yPos);
    
    // Save the PDF
    doc.save(`balance-sheet-${activeBusiness.name}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (!activeBusiness) {
    return (
      <div className="p-6">
        <p>Please select a business to view reports.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 ml-64 p-6 space-y-6">
      {/* Header Section - Hidden in print */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold">Balance Sheet</h1>
          <p className="text-muted-foreground">Financial position for {activeBusiness.name}</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Button onClick={() => exportReport('pdf')} variant="outline">
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          
          <Button onClick={() => exportReport('excel')} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          
          <Button onClick={fetchReportData} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {loading ? "Loading..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Period Selector Card - Hidden in print */}
      <Card className="print:hidden">
        <CardHeader>
          <CardTitle>Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-dates">All Dates</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="last-week">Last Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="this-quarter">This Quarter</SelectItem>
                <SelectItem value="last-quarter">Last Quarter</SelectItem>
                <SelectItem value="this-year">This Year</SelectItem>
                <SelectItem value="last-year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Dates</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchReportData} disabled={loading} variant="outline">
              {loading ? "Updating..." : "Update Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clean PDF Header - Only shown in print */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{activeBusiness.name}</h1>
        <h2 className="text-xl mb-4">Balance Sheet</h2>
        <p className="text-sm text-gray-600">
          As of: {balanceSheetData?.date || new Date().toLocaleDateString()} | 
          Generated: {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Balance Sheet */}
      <Card>
        <CardHeader>
          <CardTitle>Balance Sheet</CardTitle>
          <p className="text-sm text-muted-foreground">
            As of {balanceSheetData?.date || new Date().toLocaleDateString()} for {activeBusiness.name}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Assets */}
            <div>
              <h3 className="font-semibold text-lg mb-4 text-blue-700">ASSETS</h3>
              
              {/* Current Assets */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Current Assets</h4>
                <div className="space-y-1 ml-4">
                  {balanceSheetData?.assets?.currentAssets?.map((asset: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <span>{asset.name}</span>
                      <span>${asset.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                  <span>Total Current Assets</span>
                  <span>${balanceSheetData?.assets?.totalCurrentAssets?.toLocaleString() || '0'}</span>
                </div>
              </div>

              {/* Fixed Assets */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Fixed Assets</h4>
                <div className="space-y-1 ml-4">
                  {balanceSheetData?.assets?.fixedAssets?.map((asset: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <span>{asset.name}</span>
                      <span>${asset.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                  <span>Total Fixed Assets</span>
                  <span>${balanceSheetData?.assets?.totalFixedAssets?.toLocaleString() || '0'}</span>
                </div>
              </div>

              <div className="flex justify-between font-bold text-lg pt-2 border-t-2">
                <span>TOTAL ASSETS</span>
                <span className="text-blue-600">
                  ${balanceSheetData?.assets?.totalAssets?.toLocaleString() || '0'}
                </span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div>
              <h3 className="font-semibold text-lg mb-4 text-red-700">LIABILITIES & EQUITY</h3>
              
              {/* Current Liabilities */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Current Liabilities</h4>
                <div className="space-y-1 ml-4">
                  {balanceSheetData?.liabilities?.currentLiabilities?.map((liability: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <span>{liability.name}</span>
                      <span>${liability.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                  <span>Total Current Liabilities</span>
                  <span>${balanceSheetData?.liabilities?.totalCurrentLiabilities?.toLocaleString() || '0'}</span>
                </div>
              </div>

              {/* Long-term Liabilities */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Long-term Liabilities</h4>
                <div className="space-y-1 ml-4">
                  {balanceSheetData?.liabilities?.longTermLiabilities?.map((liability: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <span>{liability.name}</span>
                      <span>${liability.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                  <span>Total Long-term Liabilities</span>
                  <span>${balanceSheetData?.liabilities?.totalLongTermLiabilities?.toLocaleString() || '0'}</span>
                </div>
              </div>

              <div className="flex justify-between font-bold mb-6 pt-2 border-t-2">
                <span>TOTAL LIABILITIES</span>
                <span className="text-red-600">
                  ${balanceSheetData?.liabilities?.totalLiabilities?.toLocaleString() || '0'}
                </span>
              </div>

              {/* Equity */}
              <div className="mb-6">
                <h4 className="font-medium mb-2 text-green-700">Owner's Equity</h4>
                <div className="space-y-1 ml-4">
                  {balanceSheetData?.equity?.items?.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <span>{item.name}</span>
                      <span>${item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                  <span>TOTAL EQUITY</span>
                  <span className="text-green-600">
                    ${balanceSheetData?.equity?.totalEquity?.toLocaleString() || '0'}
                  </span>
                </div>
              </div>

              <div className="flex justify-between font-bold text-lg pt-4 border-t-2 border-double">
                <span>TOTAL LIABILITIES & EQUITY</span>
                <span className="text-purple-600">
                  ${((balanceSheetData?.liabilities?.totalLiabilities || 0) + (balanceSheetData?.equity?.totalEquity || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}